import type { QlcSubWidget, WidgetType } from '~~/shared/types/qlc';

export interface ParsedWidgetInfo {
    id: string;
    name: string;
}

export interface WidgetTypeResult {
    widgetId: string;
    type: WidgetType;
}

export interface WidgetStatusResult {
    widgetId: string;
    value: number;
}

export interface WidgetSubIdListResult {
    widgetId: string;
    subWidgets: QlcSubWidget[];
}

export interface LiveUpdateResult {
    widgetId: string;
    value: number;
}

/**
 * Builds the QLC+ API command to fetch the flat list of all Virtual Console widgets.
 */
export function buildGetWidgetsList(): string {
    return 'QLC+API|getWidgetsList';
}

/**
 * Parses the pipe-separated response from getWidgetsList: "ID|Name|ID|Name|..."
 */
export function parseWidgetsList(payload: string): ParsedWidgetInfo[] {
    const raw = payload.replace(/^QLC\+API\|getWidgetsList\|?/, '');
    const tokens = raw.split('|').filter(t => t.trim().length > 0);
    const widgets: ParsedWidgetInfo[] = [];

    for (let i = 0; i < tokens.length; i += 2) {
        const id = tokens[i]?.trim();
        const name = tokens[i + 1]?.trim() || `Widget ${id}`;
        if (id) {
            widgets.push({
                id,
                name,
            });
        }
    }
    return widgets;
}

/**
 * Builds the command to fetch a widget's type.
 */
export function buildGetWidgetType(widgetId: string): string {
    return `QLC+API|getWidgetType|${widgetId}`;
}

/**
 * Parses getWidgetType response.
 * Formats: "QLC+API|getWidgetType|<WidgetID>|<Type>" or simply "<Type>"
 */
export function parseWidgetTypeResponse(payload: string, requestedWidgetId?: string): WidgetTypeResult | null {
    if (payload.startsWith('QLC+API|getWidgetType|')) {
        const parts = payload.split('|');
        const widgetId = parts[2]?.trim() || requestedWidgetId || '';
        const rawType = parts[3]?.trim() || 'Unknown';
        return {
            widgetId,
            type: normalizeWidgetType(rawType),
        };
    }

    if (requestedWidgetId) {
        return {
            widgetId: requestedWidgetId,
            type: normalizeWidgetType(payload.trim()),
        };
    }

    return null;
}

/**
 * Normalizes raw QLC+ widget type string to canonical WidgetType.
 */
export function normalizeWidgetType(raw: string): WidgetType {
    const lower = raw.toLowerCase();
    if (lower.includes('button')) {
        return 'Button';
    }
    if (lower.includes('slider')) {
        return 'Slider';
    }
    if (lower.includes('knob')) {
        return 'Knob';
    }
    if (lower.includes('solo')) {
        return 'SoloFrame';
    }
    if (lower.includes('frame')) {
        return 'Frame';
    }
    if (lower.includes('animation') || lower.includes('matrix')) {
        return 'Animation';
    }
    if (lower.includes('speed') || lower.includes('dial')) {
        return 'SpeedDial';
    }
    if (lower.includes('audio')) {
        return 'AudioTriggers';
    }
    return 'Unknown';
}

/**
 * Builds the command to query the current status / value of a widget.
 */
export function buildGetWidgetStatus(widgetId: string): string {
    return `QLC+API|getWidgetStatus|${widgetId}`;
}

/**
 * Parses getWidgetStatus response.
 * Formats: "QLC+API|getWidgetStatus|<WidgetID>|<Value>" or numeric string
 */
export function parseWidgetStatusResponse(payload: string, requestedWidgetId?: string): WidgetStatusResult | null {
    if (payload.startsWith('QLC+API|getWidgetStatus|')) {
        const parts = payload.split('|');
        const widgetId = parts[2]?.trim() || requestedWidgetId || '';
        const value = Number.parseInt(parts[3]?.trim() || '0', 10);
        return {
            widgetId,
            value: Number.isNaN(value) ? 0 : Math.max(0, Math.min(255, value)),
        };
    }

    if (requestedWidgetId) {
        const value = Number.parseInt(payload.trim(), 10);
        if (!Number.isNaN(value)) {
            return {
                widgetId: requestedWidgetId,
                value: Math.max(0, Math.min(255, value)),
            };
        }
    }

    return null;
}

/**
 * Builds the command to retrieve sub-widgets/presets for Animation widgets and Frames.
 */
export function buildGetWidgetSubIdList(widgetId: string): string {
    return `QLC+API|getWidgetSubIdList|${widgetId}`;
}

/**
 * Parses getWidgetSubIdList response.
 * Formats: "QLC+API|getWidgetSubIdList|<WidgetID>|<SubID1>|<SubName1>|<SubID2>|<SubName2>..."
 */
export function parseWidgetSubIdListResponse(payload: string, requestedWidgetId?: string): WidgetSubIdListResult | null {
    let widgetId = requestedWidgetId || '';
    let rawTokens: string[];

    if (payload.startsWith('QLC+API|getWidgetSubIdList|')) {
        const parts = payload.split('|');
        widgetId = parts[2]?.trim() || widgetId;
        rawTokens = parts.slice(3);
    }
    else {
        rawTokens = payload.split('|');
    }

    const subWidgets: QlcSubWidget[] = [];
    for (let i = 0; i < rawTokens.length; i += 2) {
        const id = rawTokens[i]?.trim();
        const name = rawTokens[i + 1]?.trim() || `Sub ${id}`;
        if (id) {
            subWidgets.push({
                id,
                name,
            });
        }
    }

    return {
        widgetId,
        subWidgets,
    };
}

/**
 * Builds a Button control command: <WidgetID>|255 (press) or <WidgetID>|0 (release)
 */
export function buildButtonCommand(widgetId: string, pressed: boolean): string {
    return `${widgetId}|${pressed ? 255 : 0}`;
}

/**
 * Builds a Slider / Fader / Knob control command: <WidgetID>|<0-255>
 */
export function buildSliderCommand(widgetId: string, value: number): string {
    const clamped = Math.max(0, Math.min(255, Math.round(value)));
    return `${widgetId}|${clamped}`;
}

/**
 * Converts a hex color string (e.g. "#ff0000" or "ff0000") to integer value for QLC+ QRgb.
 */
export function hexToQlcColorInt(hex: string): number {
    const cleanHex = hex.replace('#', '').trim();
    const val = Number.parseInt(cleanHex, 16);
    return Number.isNaN(val) ? 0 : val;
}

/**
 * Builds direct QLC+ Animation widget color command:
 * <WidgetID>|MATRIX_COLOR_<1-5>|<HEX_CODE>
 */
export function buildMatrixColorCommand(
    widgetId: string,
    slot: 1 | 2 | 3 | 4 | 5,
    hex: string,
): string {
    const formattedHex = hex.startsWith('#') ? hex : `#${hex}`;
    return `${widgetId}|MATRIX_COLOR_${slot}|${formattedHex}`;
}

/**
 * Builds native QLC+ Animation widget color change command:
 * <WidgetID>|MATRIX_COLOR_CHANGE|COLOR_<slot>|<intVal>
 */
export function buildMatrixColorChangeCommand(
    widgetId: string,
    slot: 1 | 2 | 3 | 4 | 5,
    hex: string,
): string {
    const intVal = hexToQlcColorInt(hex);
    return `${widgetId}|MATRIX_COLOR_CHANGE|COLOR_${slot}|${intVal}`;
}

/**
 * Builds QLC+ Animation widget slider command (used by QLC+ web virtual console):
 * <WidgetID>|MATRIX_SLIDER|<0-255>
 */
export function buildMatrixSliderCommand(widgetId: string, value: number): string {
    const clamped = Math.max(0, Math.min(255, Math.round(value)));
    return `${widgetId}|MATRIX_SLIDER|${clamped}`;
}

/**
 * Builds native QLC+ Animation widget intensity slider command:
 * <WidgetID>|MATRIX_SLIDER_CHANGE|<0-255>
 */
export function buildMatrixSliderChangeCommand(widgetId: string, value: number): string {
    const clamped = Math.max(0, Math.min(255, Math.round(value)));
    return `${widgetId}|MATRIX_SLIDER_CHANGE|${clamped}`;
}

/**
 * Builds native QLC+ Animation algorithm combo command:
 * <WidgetID>|MATRIX_COMBO|<AnimationID>
 */
export function buildMatrixComboCommand(widgetId: string, animationId: number): string {
    return `${widgetId}|MATRIX_COMBO|${animationId}`;
}

/**
 * Builds native QLC+ Animation algorithm combo command:
 * <WidgetID>|MATRIX_COMBO_CHANGE|<AlgorithmName>
 */
export function buildMatrixComboChangeCommand(widgetId: string, algorithmName: string): string {
    return `${widgetId}|MATRIX_COMBO_CHANGE|${algorithmName}`;
}

/**
 * Builds native QLC+ Animation custom pushbutton command:
 * <WidgetID>|MATRIX_PUSHBUTTON|<ControlID>
 */
export function buildMatrixPushButtonCommand(widgetId: string, controlId: string | number): string {
    return `${widgetId}|MATRIX_PUSHBUTTON|${controlId}`;
}

/**
 * Builds native QLC+ SpeedDial factor / multiplier command:
 * <WidgetID>|SPEED_FACTOR|<factorIndex>
 * Index mapping: 2: 1/16, 3: 1/8, 4: 1/4, 5: 1/2, 6: 1, 7: 2, 8: 4, 9: 8, 10: 16
 */
export function buildSpeedDialFactorCommand(widgetId: string, factorIndex: number): string {
    return `${widgetId}|SPEED_FACTOR|${factorIndex}`;
}

/**
 * Builds native QLC+ SpeedDial time / tap duration command:
 * <WidgetID>|SPEED_TIME|<timeMs>
 */
export function buildSpeedDialTimeCommand(widgetId: string, timeMs: number): string {
    const clamped = Math.max(0, Math.round(timeMs));
    return `${widgetId}|SPEED_TIME|${clamped}`;
}

/**
 * Builds native QLC+ SpeedDial step commands (halve or double):
 * <WidgetID>|SPEED_UP or <WidgetID>|SPEED_DOWN
 */
export function buildSpeedDialStepCommand(widgetId: string, direction: 'up' | 'down'): string {
    return `${widgetId}|SPEED_${direction.toUpperCase()}`;
}

/**
 * Builds an Animation Preset / Sub-Control command.
 * Native QLC+ format (from Test_Web_API.html & webaccess): <WidgetID>|MATRIX_PUSHBUTTON|<ControlID>
 */
export function buildAnimationPresetCommand(widgetId: string, controlId: string): string {
    return `${widgetId}|MATRIX_PUSHBUTTON|${controlId}`;
}

/**
 * Builds an Animation sub-control / push-button trigger:
 * Format used natively by QLC+: <WidgetID>|MATRIX_PUSHBUTTON|<ControlID>
 */
export function buildAnimationSubControlCommand(widgetId: string, controlId: string): string {
    return `${widgetId}|MATRIX_PUSHBUTTON|${controlId}`;
}

/**
 * Builds legacy QLC+ API Animation widget control command
 */
export function buildAnimationControlApiCommand(widgetId: string, controlId: string): string {
    return `QLC+API|Animation widget control|${widgetId}|${controlId}`;
}

/**
 * Builds an Animation color command:
 * QLC+API|Animation widget color|<WidgetID>|<PrimaryHex>|<SecondaryHex>
 */
export function buildAnimationColorCommand(widgetId: string, primaryHex: string, secondaryHex: string): string {
    return `QLC+API|Animation widget color|${widgetId}|${primaryHex}|${secondaryHex}`;
}

/**
 * Builds an Animation speed command:
 * <WidgetID>|MATRIX_SLIDER_CHANGE|<speed>
 */
export function buildAnimationSpeedCommand(widgetId: string, speedValue: number): string {
    const clamped = Math.max(0, Math.min(255, Math.round(speedValue)));
    return `${widgetId}|MATRIX_SLIDER_CHANGE|${clamped}`;
}

export interface MatrixLiveUpdate {
    widgetId: string;
    type: 'color' | 'slider' | 'combo';
    slot?: 1 | 2 | 3 | 4 | 5;
    colorHex?: string;
    sliderValue?: number;
    comboValue?: string;
}

/**
 * Parses real-time animation matrix updates broadcast by QLC+:
 * - "<WidgetID>|MATRIX_COLOR_<1-5>|<#hex>"
 * - "<WidgetID>|MATRIX_SLIDER|<0-255>"
 * - "<WidgetID>|MATRIX_COMBO|<algorithm>"
 */
export function parseMatrixLiveUpdate(message: string): MatrixLiveUpdate | null {
    if (message.startsWith('QLC+API|') || message.startsWith('{')) {
        return null;
    }

    const parts = message.split('|');
    if (parts.length >= 3) {
        const widgetId = parts[0]?.trim();
        const cmd = parts[1]?.trim();
        const value = parts[2]?.trim();

        if (!widgetId || !cmd || value === undefined) {
            return null;
        }

        if (cmd.startsWith('MATRIX_COLOR_')) {
            const slotNum = Number.parseInt(cmd.replace('MATRIX_COLOR_', ''), 10);
            if (slotNum >= 1 && slotNum <= 5) {
                return {
                    widgetId,
                    type: 'color',
                    slot: slotNum as 1 | 2 | 3 | 4 | 5,
                    colorHex: value.startsWith('#') ? value : `#${value}`,
                };
            }
        }

        if (cmd === 'MATRIX_SLIDER') {
            const num = Number.parseInt(value, 10);
            if (!Number.isNaN(num)) {
                return {
                    widgetId,
                    type: 'slider',
                    sliderValue: Math.max(0, Math.min(255, num)),
                };
            }
        }

        if (cmd === 'MATRIX_COMBO') {
            return {
                widgetId,
                type: 'combo',
                comboValue: value,
            };
        }
    }

    return null;
}

export interface SpeedDialLiveUpdate {
    widgetId: string;
    type: 'factor' | 'time' | 'state';
    factorIndex?: number;
    timeMs?: number;
    bpm?: number;
}

/**
 * Normalizes raw factor representation (enum index 2..10, fraction string, or numeric) to enum index.
 */
export function parseSpeedDialFactorValue(raw: string): number | null {
    const trimmed = raw.trim();

    if (trimmed === '1/16' || trimmed === '0.0625') {
        return 2;
    }
    if (trimmed === '1/8' || trimmed === '0.125') {
        return 3;
    }
    if (trimmed === '1/4' || trimmed === '0.25') {
        return 4;
    }
    if (trimmed === '1/2' || trimmed === '0.5') {
        return 5;
    }
    if (trimmed === '1' || trimmed === '1x') {
        return 6;
    }
    if (trimmed === '2x') {
        return 7;
    }
    if (trimmed === '4x') {
        return 8;
    }
    if (trimmed === '8x') {
        return 9;
    }
    if (trimmed === '16x') {
        return 10;
    }

    const num = Number.parseInt(trimmed, 10);
    if (!Number.isNaN(num)) {
        if (num >= 2 && num <= 10) {
            return num;
        }
        if (num === 1) {
            return 6;
        }
        if (num === 16) {
            return 10;
        }
    }

    return null;
}

/**
 * Parses real-time SpeedDial updates broadcast by QLC+:
 * - "<WidgetID>|SPEED_STATE|<ms>|<factor>"
 * - "<WidgetID>|SPEED_FACTOR|<index>"
 * - "<WidgetID>|SPEED_TIME|<ms>"
 */
export function parseSpeedDialLiveUpdate(message: string): SpeedDialLiveUpdate | null {
    if (message.startsWith('QLC+API|') || message.startsWith('{')) {
        return null;
    }

    const parts = message.split('|');
    if (parts.length >= 3) {
        const widgetId = parts[0]?.trim();
        const cmd = parts[1]?.trim();
        const value = parts[2]?.trim();

        if (!widgetId || !cmd || value === undefined) {
            return null;
        }

        // Handle: <WidgetID>|SPEED_STATE|<ms>|<factor>
        if (cmd === 'SPEED_STATE' && parts.length >= 4) {
            const rawMs = Number.parseFloat(parts[2]?.trim() || '');
            const rawFactor = parts[3]?.trim() || '';
            const factorIndex = parseSpeedDialFactorValue(rawFactor);
            const timeMs = Number.isNaN(rawMs) || rawMs <= 0 ? undefined : Math.round(rawMs);
            const bpm = timeMs ? Math.round(60000 / timeMs) : undefined;

            return {
                widgetId,
                type: 'state',
                factorIndex: factorIndex ?? undefined,
                bpm,
                timeMs,
            };
        }

        if (cmd === 'SPEED_FACTOR') {
            const factorIndex = parseSpeedDialFactorValue(value);
            if (factorIndex !== null) {
                return {
                    widgetId,
                    type: 'factor',
                    factorIndex,
                };
            }
        }

        if (cmd === 'SPEED_TIME') {
            const timeMs = Number.parseInt(value, 10);
            if (!Number.isNaN(timeMs) && timeMs > 0) {
                return {
                    widgetId,
                    type: 'time',
                    timeMs,
                    bpm: Math.round(60000 / timeMs),
                };
            }
        }
    }

    return null;
}

/**
 * Parses real-time live value update: "<WidgetID>|<Value>"
 */
export function parseLiveWidgetUpdate(message: string): LiveUpdateResult | null {
    if (message.startsWith('QLC+API|') || message.startsWith('{')) {
        return null;
    }

    const parts = message.split('|');
    if (parts.length === 2) {
        const widgetId = parts[0]?.trim();
        const value = Number.parseInt(parts[1]?.trim() || '', 10);
        if (widgetId && !Number.isNaN(value)) {
            return {
                widgetId,
                value: Math.max(0, Math.min(255, value)),
            };
        }
    }

    return null;
}

/**
 * Builds the command to fetch a widget's associated function ID.
 * Format: "QLC+API|getWidgetFunction|<widgetId>"
 */
export function buildGetWidgetFunction(widgetId: string): string {
    return `QLC+API|getWidgetFunction|${widgetId}`;
}

export interface WidgetFunctionResult {
    widgetId: string;
    functionId: string;
    functionType?: string;
    functionName?: string;
}

/**
 * Parses getWidgetFunction response.
 * Formats: "QLC+API|getWidgetFunction|<widgetId>|<functionId>|<functionType>|<functionName>"
 */
export function parseWidgetFunctionResponse(payload: string): WidgetFunctionResult | null {
    if (payload.startsWith('QLC+API|getWidgetFunction|')) {
        const parts = payload.split('|');
        const widgetId = parts[2]?.trim();
        const functionId = parts[3]?.trim();
        if (widgetId && functionId && functionId !== '0') {
            return {
                widgetId,
                functionId,
                functionType: parts[4]?.trim(),
                functionName: parts[5]?.trim(),
            };
        }
    }
    return null;
}

export interface FunctionStatusUpdate {
    functionId: string;
    status: 'Running' | 'Stopped';
}

/**
 * Parses real-time FUNCTION status updates broadcast by QLC+:
 * "FUNCTION|<id>|Running" or "FUNCTION|<id>|Stopped"
 * Case-insensitive matching. Also supports "QLC+API|getFunctionStatus|<id>|<status>".
 */
export function parseFunctionStatusUpdate(message: string): FunctionStatusUpdate | null {
    const clean = message.trim();
    if (clean.startsWith('FUNCTION|')) {
        const parts = clean.split('|');
        if (parts.length >= 3) {
            const functionId = parts[1]?.trim();
            const rawStatus = parts[2]?.trim().toLowerCase();
            if (functionId && (rawStatus === 'running' || rawStatus === 'stopped')) {
                return {
                    functionId,
                    status: rawStatus === 'running' ? 'Running' : 'Stopped',
                };
            }
        }
    }

    if (clean.startsWith('QLC+API|getFunctionStatus|')) {
        const parts = clean.split('|');
        if (parts.length >= 4) {
            const functionId = parts[2]?.trim();
            const rawStatus = parts[3]?.trim().toLowerCase();
            if (functionId && (rawStatus === 'running' || rawStatus === 'stopped')) {
                return {
                    functionId,
                    status: rawStatus === 'running' ? 'Running' : 'Stopped',
                };
            }
        }
    }

    return null;
}

export interface ButtonStatusUpdate {
    widgetId: string;
    value: number;
}

/**
 * Parses BUTTON status update from QLC+:
 * "<widgetId>|BUTTON|<value>"
 */
export function parseButtonStatusUpdate(message: string): ButtonStatusUpdate | null {
    const parts = message.trim().split('|');
    if (parts.length >= 3 && parts[1]?.trim().toUpperCase() === 'BUTTON') {
        const widgetId = parts[0]?.trim();
        const rawVal = parts[2]?.trim();
        const value = Number.parseInt(rawVal || '', 10);
        if (widgetId && !Number.isNaN(value)) {
            return {
                widgetId,
                value: value > 0 ? 255 : 0,
            };
        }
    }
    return null;
}
